<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <div class="row">
        <div class="col-md-12">
          <h3>
            <i class="fa fa-fw fa-handshake"></i> {{ partner.name || _('Partner') }}
            <span :class="statusClass(partner.status)" v-if="partner.status">{{ statusLabel(partner.status) }}</span>
          </h3>
        </div>
      </div>
      <editor-tabs :tabs="tabList" :selected="selectedTab" :id-prefix="tabIdPrefix" v-on:select="tab" />
      <div class="tab-content">
        <transition-group name="fade">
          <div v-bind="tabPanel('general')" class="tab-pane active" id="general" v-if="isTabActive('general')" key="general">
            <div class="row">
              <div class="col-sm-6 leftcol">
                <div class="form-group">
                  <label for="partnerName">{{ _('Name') }}</label>
                  <input class="form-control" id="partnerName" type="text" v-model="partner.name" />
                </div>
                <div class="form-group">
                  <label for="partnerWebsite">{{ _('Website') }}</label>
                  <input class="form-control" id="partnerWebsite" type="text" v-model.trim="partner.website" placeholder="https://" />
                </div>
                <div class="form-group">
                  <label for="partnerContactName">{{ _('Contact person') }}</label>
                  <input class="form-control" id="partnerContactName" type="text" v-model="partner.contact_name" />
                </div>
                <div class="form-group">
                  <label for="partnerContactEmail">{{ _('Contact email') }}</label>
                  <input class="form-control" id="partnerContactEmail" type="email" v-model.trim="partner.contact_email" />
                </div>
                <div class="form-group">
                  <label for="partnerNotes">{{ _('Notes') }}</label>
                  <textarea class="form-control" id="partnerNotes" rows="4" v-model="partner.notes"></textarea>
                </div>
              </div>
              <div class="col-sm-6 rightcol">
                <div class="panel panel-default partner-status-panel">
                  <div class="panel-heading">
                    <h4 class="panel-title">{{ _('Status') }}: <span :class="statusClass(partner.status)">{{ statusLabel(partner.status) }}</span></h4>
                  </div>
                  <div class="panel-body">
                    <p class="help-block">{{ statusHelp }}</p>
                    <p v-if="partner.verified_at">{{ _('Verified') }}: {{ formatApiTime(partner.verified_at) }}<span v-if="partner.verified_by"> ({{ partner.verified_by }})</span></p>
                    <template v-if="partner.status === 'pilot'">
                      <p><strong>{{ _('Before verifying, check from the pilot integration\'s Usage tab that:') }}</strong></p>
                      <ul class="partner-checklist">
                        <li>{{ _('the pilot shops have had real traffic') }}</li>
                        <li>{{ _('client and server errors are rare') }}</li>
                        <li>{{ _('the integration rarely hits the rate limits') }}</li>
                        <li>{{ _('the pilot shops are happy with the integration') }}</li>
                      </ul>
                    </template>
                    <div class="form-group" v-if="partner.review_notes">
                      <label>{{ _('Review notes') }}</label>
                      <p class="partner-review-notes">{{ partner.review_notes }}</p>
                    </div>
                    <div class="partner-status-buttons">
                      <button v-for="transition in statusTransitions" :key="transition.status + transition.label" type="button" :class="'btn btn-' + transition.className" v-on:click="changeStatus(transition)" :disabled="loading || saving || !partner.id">
                        <i :class="'fa fa-fw fa-' + transition.icon"></i> {{ transition.label }}
                      </button>
                    </div>
                  </div>
                </div>
                <div class="form-group">
                  <label for="partnerPilotMaxShops">{{ _('Pilot shop cap') }}</label>
                  <input class="form-control" id="partnerPilotMaxShops" type="number" min="0" step="1" v-model="partner.pilot_max_shops" />
                  <p class="help-block">{{ _('How many shops a pilot partner may connect to in total. Verified partners have no cap.') }}</p>
                </div>
                <h4>{{ _('Rate limits') }}</h4>
                <div class="form-group">
                  <label for="partnerTier">{{ _('Rate limit tier') }}</label>
                  <input class="form-control" id="partnerTier" type="text" v-model.trim="partner.tier" />
                </div>
                <div class="form-group">
                  <label for="partnerLimitRpm">{{ _('Requests per minute') }}</label>
                  <input class="form-control" id="partnerLimitRpm" type="number" min="0" step="1" v-model="partner.limit_rpm" :placeholder="_('Tier default')" />
                </div>
                <div class="form-group">
                  <label for="partnerLimitDaily">{{ _('Requests per day') }}</label>
                  <input class="form-control" id="partnerLimitDaily" type="number" min="0" step="1" v-model="partner.limit_daily" :placeholder="_('Tier default')" />
                </div>
                <div class="form-group">
                  <label for="partnerLimitConcurrency">{{ _('Concurrent requests') }}</label>
                  <input class="form-control" id="partnerLimitConcurrency" type="number" min="0" step="1" v-model="partner.limit_concurrency" :placeholder="_('Tier default')" />
                  <p class="help-block">{{ _('Leave a limit empty to use the tier default.') }}</p>
                </div>
                <p class="text-muted" v-if="partner.created_at">{{ _('Created') }}: {{ formatApiTime(partner.created_at) }}</p>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('members')" class="tab-pane active" id="members" v-if="isTabActive('members')" key="members">
            <p class="help-block">{{ _('Members are matched by email to Johku users. A member can connect the partner\'s integrations to the shops where they are at least a standard user.') }}</p>
            <div class="partner-table-wrap" v-if="members.length">
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th>{{ _('Email') }}</th>
                    <th>{{ _('Role') }}</th>
                    <th>{{ _('Shops') }}</th>
                    <th>{{ _('Added') }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="member in members" :key="member.id">
                    <td>{{ member.email }}</td>
                    <td>{{ roleLabel(member.role) }}</td>
                    <td>
                      <span class="text-muted" v-if="!member.shops || !member.shops.length">{{ _('No shops') }}</span>
                      <div v-for="shop in member.shops || []" :key="shop.shop">{{ shop.shop }} <span class="text-muted">({{ userLevelLabel(shop.userlevel) }})</span></div>
                    </td>
                    <td>
                      {{ formatApiTime(member.added_at) }}
                      <div class="text-muted small" v-if="member.added_by">{{ member.added_by }}</div>
                    </td>
                    <td class="text-right">
                      <button type="button" class="btn btn-danger btn-sm" v-on:click="removeMember(member)" :disabled="saving"><i class="fa fa-fw fa-trash"></i> {{ _('Remove') }}</button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="text-muted" v-else>{{ _('No members yet.') }}</p>
            <h4>{{ _('Add a member') }}</h4>
            <div class="row">
              <div class="col-sm-6">
                <div class="form-group">
                  <label for="partnerMemberEmail">{{ _('Email') }}</label>
                  <input class="form-control" id="partnerMemberEmail" type="email" v-model.trim="newMember.email" v-on:keydown.enter.prevent="addMember" />
                </div>
              </div>
              <div class="col-sm-3">
                <div class="form-group">
                  <label for="partnerMemberRole">{{ _('Role') }}</label>
                  <select class="form-control" id="partnerMemberRole" v-model="newMember.role">
                    <option value="developer">{{ roleLabel('developer') }}</option>
                    <option value="owner">{{ roleLabel('owner') }}</option>
                  </select>
                </div>
              </div>
              <div class="col-sm-3">
                <label class="hidden-xs">&nbsp;</label>
                <button type="button" class="btn btn-success btn-block" v-on:click="addMember" :disabled="saving || !newMember.email"><i class="fa fa-fw fa-plus"></i> {{ _('Add') }}</button>
              </div>
            </div>
          </div>
          <div v-bind="tabPanel('integrations')" class="tab-pane active" id="integrations" v-if="isTabActive('integrations')" key="integrations">
            <div class="partner-tab-buttons">
              <button type="button" class="btn btn-success" v-on:click="createIntegration" :disabled="!partner.id"><i class="fa fa-fw fa-plus"></i> {{ _('New integration') }}</button>
            </div>
            <div class="partner-table-wrap" v-if="integrations.length">
              <table class="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>{{ _('Name') }}</th>
                    <th>{{ _('Status') }}</th>
                    <th>{{ _('Scopes') }}</th>
                    <th class="text-right">{{ _('Keys') }}</th>
                    <th class="text-right">{{ _('Connected shops') }}</th>
                    <th>{{ _('Last used') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="partner-row" v-for="integration in integrations" :key="integration.id" v-on:click="openIntegration(integration)">
                    <td>{{ integration.name }}</td>
                    <td><span :class="statusClass(integration.status)">{{ statusLabel(integration.status) }}</span></td>
                    <td>
                      <div v-for="scope in normalizeScopes(integration.scopes)" :key="scope" :title="scope">{{ scopeLabel(scope) }}</div>
                    </td>
                    <td class="text-right">{{ keyCount(integration.keys) }}</td>
                    <td class="text-right">{{ formatCount(integration.connected_shops) }}</td>
                    <td>{{ formatApiTime(integration.last_used_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="text-muted" v-else>{{ _('No integrations yet.') }}</p>
          </div>
        </transition-group>
      </div>
    </div>
    <nav class="save-bar">
      <div class="flex-row space-between">
        <div class="flex-row"></div>
        <div class="button-col">
          <button class="btn btn-default" v-on:click="close"><i class="fa fa-fw fa-close"></i> {{ _('Close') }}</button>
          <button class="btn btn-success" v-on:click="save" :disabled="!isSaveButtonEnabled || !partner.id"><i :class="'fa fa-fw fa-' + saveButtonIcon"></i> {{ saveButtonText }}</button>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import axios from 'axios'
import debounce from 'lodash/debounce'
import changewatcher from '~/lib/changewatcher'
import components from '~/lib/components'
import escapeHtml from '~/lib/escapehtml'
import partnerapi, { PARTNER_SCOPES } from '~/lib/partnerapi'

export default {
  mixins: [changewatcher, components, partnerapi],
  data () {
    return {
      activeTab: 'general',
      partner: this.emptyPartner(),
      members: [],
      integrations: [],
      newMember: {
        email: '',
        role: 'developer'
      }
    }
  },
  computed: {
    tabList () {
      return this.editorTabs([
        { id: 'general', label: this._('Overview') },
        { id: 'members', label: this._('Members'), badge: this.members.length || undefined, badgeClass: 'btn-default' },
        { id: 'integrations', label: this._('Integrations'), badge: this.integrations.length || undefined, badgeClass: 'btn-default' }
      ])
    },
    requestedId () {
      return this.component && this.component.initData ? parseInt(this.component.initData.partnerId || 0, 10) : 0
    },
    statusHelp () {
      switch (this.partner.status) {
        case 'pending':
          return this._('The partner can only use test keys. Start a pilot to allow live keys on a limited number of shops.')
        case 'pilot':
          return this._('The partner can use live keys on a limited number of shops. Verify the partner when the pilot has gone well.')
        case 'verified':
          return this._('Shops see the partner\'s integrations as verified by Johku. There is no cap on shops.')
        case 'suspended':
          return this._('All of the partner\'s keys are refused. Shop connections are kept and work again when the partner is reactivated.')
      }
      return ''
    },
    // pending → pilot → verified, suspend from any of them, reactivate a suspended partner
    statusTransitions () {
      const ret = []
      switch (this.partner.status) {
        case 'pending':
          ret.push({ status: 'pilot', label: this._('Start pilot'), className: 'info', icon: 'play' })
          break
        case 'pilot':
          ret.push({ status: 'verified', label: this._('Verify'), className: 'success', icon: 'check' })
          break
        case 'suspended':
          ret.push({ status: this.partner.verified_at ? 'verified' : 'pilot', label: this._('Reactivate'), className: 'success', icon: 'undo' })
          break
      }
      if (this.partner.status && this.partner.status !== 'suspended') {
        ret.push({ status: 'suspended', label: this._('Suspend'), className: 'danger', icon: 'ban' })
      }
      return ret
    }
  },
  methods: {
    emptyPartner () {
      return {
        id: 0,
        name: '',
        website: '',
        contact_name: '',
        contact_email: '',
        notes: '',
        tier: '',
        limit_rpm: null,
        limit_daily: null,
        limit_concurrency: null,
        status: '',
        pilot_max_shops: null,
        verified_by: null,
        verified_at: null,
        review_notes: '',
        created_at: null
      }
    },
    nullableInt (value) {
      if (value === null || value === undefined || value === '') {
        return null
      }
      const number = parseInt(value, 10)
      return Number.isNaN(number) ? null : number
    },
    getSaveData () {
      return {
        name: (this.partner.name || '').trim(),
        website: this.partner.website || '',
        contact_name: this.partner.contact_name || '',
        contact_email: this.partner.contact_email || '',
        notes: this.partner.notes || '',
        tier: this.partner.tier || '',
        limit_rpm: this.nullableInt(this.partner.limit_rpm),
        limit_daily: this.nullableInt(this.partner.limit_daily),
        limit_concurrency: this.nullableInt(this.partner.limit_concurrency),
        pilot_max_shops: this.nullableInt(this.partner.pilot_max_shops)
      }
    },
    applyPartner (data) {
      const partner = Object.assign(this.emptyPartner(), data || {})
      this.members = Array.isArray(partner.members) ? partner.members : []
      this.integrations = Array.isArray(partner.integrations) ? partner.integrations : []
      delete partner.members
      delete partner.integrations
      for (const key of ['name', 'website', 'contact_name', 'contact_email', 'notes', 'tier', 'review_notes']) {
        partner[key] = partner[key] || ''
      }
      this.partner = partner
      this.storeState()
    },
    async loadPartner (id) {
      if (!id) {
        return
      }
      this.loading = true
      try {
        const { data } = await axios.get(this.baseDomain + this.shopId + '/admin/partners/' + id + '.json?apikey=' + this.apiKey)
        this.applyPartner(data)
      } catch (e) {
        this.showError(e, this._('Loading the partner failed.'))
      } finally {
        this.loading = false
      }
    },
    partnerUrl () {
      return this.baseDomain + this.shopId + '/admin/partners/' + this.partner.id + '.json?apikey=' + this.apiKey
    },
    // A PATCH with an operation, then the partner is loaded again
    async patchPartner (payload, successMessage) {
      this.saving = true
      try {
        await axios.patch(this.partnerUrl(), payload)
        await this.loadPartner(this.partner.id)
        this.EventBus.$emit('partnerUpdate')
        if (successMessage) {
          this.showNotification(successMessage)
        }
        return true
      } catch (e) {
        this.showError(e, null, true)
        return false
      } finally {
        this.saving = false
      }
    },
    async save () {
      if (!this.partner.id) {
        return
      }
      const ok = await this.patchPartner({ operation: 'update', ...this.getSaveData() }, this._('Changes saved successfully!'))
      if (ok) {
        this.saved = true
        setTimeout(() => { this.saved = false }, 1000)
      }
    },
    changeStatus (transition) {
      this.showDialog({
        title: transition.label + ': ' + this.partner.name,
        fields: [
          { name: 'review_notes', type: 'textarea', title: this._('Review notes'), rows: 4, defaultValue: this.partner.review_notes || '' }
        ],
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: transition.label,
            class: transition.className === 'danger' ? 'danger' : 'success',
            icon: transition.icon,
            isSubmit: true,
            handler: (data) => {
              this.patchPartner({ operation: 'status', status: transition.status, review_notes: data.review_notes || '' }, this._('Status changed.'))
            }
          }
        ]
      })
    },
    roleLabel (role) {
      if (role === 'owner') {
        return this._('Owner')
      }
      if (role === 'developer') {
        return this._('Developer')
      }
      return role || ''
    },
    userLevelLabel (level) {
      const value = parseInt(level, 10)
      const names = {
        2: this._('POS user'),
        3: this._('Standard user'),
        7: this._('Administrator'),
        10: this._('System Administrator')
      }
      return names[value] || String(level ?? '')
    },
    async addMember () {
      const email = (this.newMember.email || '').trim().toLowerCase()
      if (!email || !this.partner.id) {
        return
      }
      const ok = await this.patchPartner({ operation: 'add_member', email, role: this.newMember.role }, this._('Member added.'))
      if (ok) {
        this.newMember.email = ''
      }
    },
    removeMember (member) {
      this.showDialog({
        title: this._('Remove %s from the partner?').replace('%s', member.email),
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Remove'),
            class: 'danger',
            icon: 'trash',
            handler: () => {
              this.patchPartner({ operation: 'remove_member', member_id: member.id }, this._('Member removed.'))
            }
          }
        ]
      })
    },
    keyCount (keys) {
      if (Array.isArray(keys)) {
        return this.formatCount(keys.length)
      }
      return this.formatCount(keys)
    },
    openIntegration (integration) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'AdminPartnerIntegrationDetails',
        title: this._('Integration') + ': ' + integration.name,
        width: 6,
        data: {
          integrationId: integration.id,
          trail: this.componentTrail
        }
      })
    },
    createIntegration () {
      const fields = [
        { name: 'name', type: 'text', title: this._('Name') },
        { name: 'description', type: 'textarea', title: this._('Description'), rows: 3, help: this._('Shops see the name and the description when they give consent.') },
        { name: 'website', type: 'text', title: this._('Website'), placeholder: 'https://' },
        { type: 'h4', text: escapeHtml(this._('Scopes')) },
        { type: 'info', text: escapeHtml(this._('The most the integration may ask a shop for. Each shop decides what it grants.')), hideIcon: true }
      ]
      PARTNER_SCOPES.forEach((s, index) => {
        fields.push({
          name: 'scope_' + index,
          type: 'checkbox',
          title: escapeHtml(this._(s.label)) + ' <code>' + escapeHtml(s.scope) + '</code>',
          defaultValue: s.scope === 'catalog:read' || s.scope === 'availability:read' ? '1' : '0'
        })
      })
      this.showDialog({
        title: this._('New integration'),
        fields,
        buttons: [
          {
            title: this._('Cancel'),
            icon: 'ban',
            handler: () => {}
          },
          {
            title: this._('Create'),
            class: 'success',
            icon: 'plus',
            requiredFields: ['name'],
            handler: (data) => {
              const scopes = PARTNER_SCOPES.filter((s, index) => String(data['scope_' + index]) === '1').map(s => s.scope)
              this.saveIntegration({
                partner_id: this.partner.id,
                name: (data.name || '').trim(),
                description: data.description || '',
                website: (data.website || '').trim(),
                scopes
              })
            }
          }
        ]
      })
    },
    async saveIntegration (payload) {
      try {
        const { data } = await axios.post(this.baseDomain + this.shopId + '/admin/integrations.json?apikey=' + this.apiKey, payload)
        await this.loadPartner(this.partner.id)
        this.EventBus.$emit('partnerUpdate')
        if (data && data.id) {
          this.openIntegration(data)
        }
      } catch (e) {
        this.showError(e, this._('Creating the integration failed.'), true)
      }
    }
  },
  created () {
    this.loadPartner(this.requestedId)
  },
  mounted () {
    if (!this.events) {
      const events = {}
      events.partnerIntegrationUpdate = debounce(() => this.loadPartner(this.partner.id), 200)
      this.events = events
    }
    this.enableEventBus()
  },
  async updated () {
    const current = parseInt(this.partner.id || 0, 10)
    if (this.requestedId && current !== this.requestedId && !this.loading) {
      await this.loadPartner(this.requestedId)
    }
  }
}
</script>

<style scoped>
.partner-table-wrap {
  overflow-x: auto;
}
.partner-row {
  cursor: pointer;
}
.partner-tab-buttons {
  margin: 10px 0;
}
.partner-status-panel .panel-title .label {
  margin-left: 4px;
}
.partner-status-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.partner-checklist {
  padding-left: 20px;
}
.partner-review-notes {
  white-space: pre-wrap;
}
h3 .label {
  font-size: 50%;
  vertical-align: middle;
}
</style>
